// Slice H2.1 — cross-page intent handshake for opening the curated
// template gallery from off-page surfaces (BacktestPanel, ConvertPage).
//
// Background.
//   The H2 gallery lives inside `StrategyBuilderPage.tsx`. The H1
//   suggestion panels live inside `BacktestPanel.tsx` (in the Backtest
//   tab) and `ConvertPage.tsx` (in the Convert tab). Both H1 surfaces
//   want to render an "actionable CTA" that opens the H2 gallery —
//   but the gallery component isn't mounted from those pages.
//
//   The app uses a lightweight hash-router (`window.location.hash =
//   'builder'`). Cross-page navigation works, but we also need to
//   tell the Builder page "open the gallery when you mount." Three
//   approaches considered:
//
//     1. Query/hash parameters — adds noise to the URL surface and
//        couples the gallery state to URL parsing.
//     2. Window events — listener doesn't exist until Builder mounts;
//        events fired before mount are lost.
//     3. localStorage intent + hash navigation — Builder reads on
//        mount, clears, and the URL stays clean.
//
//   This module implements approach #3. Tiny + zero-dependency +
//   testable.
//
// Lifecycle.
//   1. H1 suggestion CTA clicked → `requestOpenCuratedGallery(...)`
//      sets the intent + writes the hash navigation.
//   2. App re-routes to the Builder page via the hash sync.
//   3. StrategyBuilderPage mount-effect calls `consumePendingCuratedGalleryIntent()`
//      → if intent present, open the gallery with the recorded
//      entry point + clear the intent (single-use).
//
// Scope guardrails.
//   • Pure module, zero deps beyond browser APIs.
//   • No reactivity / no subscribers — single-use intent only.
//   • Safe to call from SSR / test envs (guarded against missing
//     window / localStorage).

const STORAGE_KEY = 'tradeatory.openCuratedGalleryIntent';

/** What surface fired the intent. Mirrors the gallery component's
 *  entryPoint values so the consumer can pass it through verbatim. */
export type CuratedGalleryIntentEntryPoint =
  | 'h1-suggestion'
  | 'toolbar'
  | 'empty-builder'
  | 'ai-optimize-seed';

interface PendingIntent {
  entryPoint: CuratedGalleryIntentEntryPoint;
  /** Wall-clock timestamp for telemetry + stale-intent cleanup. */
  ts: number;
}

/** TTL for a pending intent. If the Builder page doesn't mount within
 *  this window after the intent was set, treat it as stale and drop.
 *  Prevents weird "I opened a tab 2 days ago" replays after a session
 *  restore. */
const INTENT_TTL_MS = 60_000; // 1 minute

/** Set the pending intent. Caller is responsible for navigating to
 *  the Builder page afterward (e.g. `window.location.hash = 'builder'`).
 *  Returns false if localStorage isn't available (SSR / private mode),
 *  in which case the caller should fall back to direct navigation
 *  without auto-opening the gallery. */
export function requestOpenCuratedGallery(
  entryPoint: CuratedGalleryIntentEntryPoint = 'h1-suggestion',
): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const intent: PendingIntent = { entryPoint, ts: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
    return true;
  } catch {
    return false;
  }
}

/** Read + clear the pending intent. Called by StrategyBuilderPage on
 *  mount. Returns the entry point if a fresh intent is present, null
 *  otherwise. Single-use: the intent is cleared whether stale or fresh
 *  so a refresh on the Builder page doesn't re-open the gallery. */
export function consumePendingCuratedGalleryIntent(): CuratedGalleryIntentEntryPoint | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Clear unconditionally — single-use semantic. Even on parse
    // failure or stale TTL, dropping the bad value is the right
    // recovery.
    window.localStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as PendingIntent;
    if (!parsed || typeof parsed.entryPoint !== 'string') return null;
    if (!parsed.ts || Date.now() - parsed.ts > INTENT_TTL_MS) return null;
    // Validate entry-point against the known enum (defensive).
    const allowed: CuratedGalleryIntentEntryPoint[] = [
      'h1-suggestion', 'toolbar', 'empty-builder', 'ai-optimize-seed',
    ];
    if (!allowed.includes(parsed.entryPoint)) return null;
    return parsed.entryPoint;
  } catch {
    return null;
  }
}

/** Peek at the pending intent WITHOUT clearing it. Used by tests +
 *  defensive UI surfaces that want to know an intent is pending. */
export function peekPendingCuratedGalleryIntent(): CuratedGalleryIntentEntryPoint | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingIntent;
    if (!parsed?.entryPoint || !parsed.ts) return null;
    if (Date.now() - parsed.ts > INTENT_TTL_MS) return null;
    return parsed.entryPoint;
  } catch {
    return null;
  }
}

/** Test-only — clear any pending intent between independent tests. */
export function _clearPendingCuratedGalleryIntentForTests(): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/** Convenience: request + navigate in one call. Use from H1 suggestion
 *  CTA handlers. Sets the intent first (so the Builder page can pick
 *  it up on mount), then sets the location hash to `#builder`. */
export function requestOpenCuratedGalleryAndNavigate(
  entryPoint: CuratedGalleryIntentEntryPoint = 'h1-suggestion',
): void {
  requestOpenCuratedGallery(entryPoint);
  // Hash navigation matches the app's existing pseudo-router pattern
  // (see App.tsx — the tab-from-hash sync picks this up).
  if (typeof window !== 'undefined') {
    window.location.hash = 'builder';
  }
}
